'use client';
import { useTranslation } from '@root/src/i18n/client';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { slugify } from '@root/src/lib/slugify';

interface Topic {
  id: string | number;
  name: string;
  slug: string | null;
  description?: string;
  image?: string;
}

interface TopicsListProps {
  topics: Topic[];
  onSelectTopic: (topic: Topic) => void;
}

export default function TopicsList({ topics, onSelectTopic }: TopicsListProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  if (!topics || topics.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[#666]">{t('common.noData', 'Không có dữ liệu')}</p>
      </div>
    );
  }

  const handleTopicClick = (topic: Topic) => {
    const resolvedSlug = topic.slug || slugify(topic.name);

    if (!resolvedSlug) {
      return;
    }

    onSelectTopic({ ...topic, slug: resolvedSlug });
    router.push(`/topic/${resolvedSlug}`);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {topics.map((topic) => (
        <article
          key={topic.id}
          className="group overflow-hidden rounded-[24px] bg-white shadow-sm hover:shadow-lg transition-shadow duration-300 cursor-pointer"
          onClick={() => handleTopicClick(topic)}
        >
          {/* Image Placeholder */}
          <div className="relative h-48 bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8] overflow-hidden">
            {topic.image ? (
              <img
                src={topic.image}
                alt={topic.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-5xl text-[#223A27]/20">
                📚
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-[#223A27] group-hover:text-[#1a2b1f] transition-colors line-clamp-2">
              {topic.name}
            </h3>

            {topic.description && (
              <p className="text-sm text-[#666] leading-6 line-clamp-3">
                {topic.description}
              </p>
            )}

            <div className="mt-auto pt-4 border-t border-[#E8DCC8]">
              <button className="text-sm font-semibold text-[#223A27] hover:text-[#1a2b1f] transition-colors flex items-center gap-1">
                {t('lytra.expReadMore', 'Xem chi tiết')}
                <span className="text-lg">→</span>
              </button>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
